import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import { typeEstruturaCompraInsumos, typeInsumos } from './index';
import { HeaderDef } from './gerarPDFCompraInsumos';
import { logo_ttivos_base64 } from '../../../../../../../Components/GerarExcel/meta';
import secureLocalStorage from "react-secure-storage";

const TOTAL_COLS = 8;
const LAST_COL_LETTER = 'H';

const formatarComposicao = (composicao: any[], composicao_bioinsumo: any[]): string => {
    const hasComposicao = Array.isArray(composicao) && composicao.length > 0;
    const hasBioinsumo = Array.isArray(composicao_bioinsumo) && composicao_bioinsumo.length > 0;

    if (hasComposicao) {
        return composicao
            .filter((c: any) => c?.insumo_nome)
            .map((c: any) => `${c.insumo_nome}${c?.concentracao != null ? ` | C: ${c.concentracao}%` : ''}`)
            .join(' • ') || '-';
    }
    if (hasBioinsumo) {
        return composicao_bioinsumo
            .filter((c: any) => c?.microrganismo_nome)
            .map((c: any) => `${c.microrganismo_nome}${c?.concentracao != null ? ` | C: ${c.concentracao}` : ''}`)
            .join(' • ') || '-';
    }
    return '-';
};

export const gerarExcelCompraInsumos = async (
    dados: (typeEstruturaCompraInsumos | typeInsumos)[],
    headerInfo?: HeaderDef
) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Compra de Insumos', {
        views: [{ showGridLines: false }]
    });

    worksheet.columns = [
        { header: '', key: 'col1', width: 30 }, // Insumo
        { header: '', key: 'col2', width: 18 }, // Tipo
        { header: '', key: 'col3', width: 20 }, // Classe Agronômica
        { header: '', key: 'col4', width: 45 }, // Composição
        { header: '', key: 'col5', width: 18 }, // Quantidade
        { header: '', key: 'col6', width: 5  }, // padding
        { header: '', key: 'col7', width: 5  }, // padding
        { header: '', key: 'col8', width: 5  }, // padding
    ];

    let currentRow = 1;

    // ** HEADER **
    worksheet.getRow(currentRow).height = 40;
    worksheet.getRow(currentRow + 1).height = 30;
    worksheet.getRow(currentRow + 2).height = 20;

    const headerImageId = workbook.addImage({
        base64: logo_ttivos_base64,
        extension: 'png',
    });

    worksheet.addImage(headerImageId, {
        tl: { col: 2.2, row: 0 },
        ext: { width: 121, height: 50 },
        editAs: 'LOGO TTIVOS'
    });
    worksheet.mergeCells(`A${currentRow}:${LAST_COL_LETTER}${currentRow}`);
    const logoRow = worksheet.getRow(currentRow);
    logoRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
    for (let i = 1; i <= TOTAL_COLS; i++) {
        logoRow.getCell(i).border = {
            bottom: { style: 'thin', color: { argb: 'FFD3D3D3' } }
        };
    }
    currentRow++;

    worksheet.mergeCells(`A${currentRow}:${LAST_COL_LETTER}${currentRow}`);
    const titleRow = worksheet.getRow(currentRow);
    titleRow.getCell(1).value = headerInfo?.titulo || 'Relatório de Compra de Insumos';
    titleRow.getCell(1).font = { size: 16, bold: true };
    titleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
    for (let i = 1; i <= TOTAL_COLS; i++) {
        titleRow.getCell(i).border = {
            bottom: { style: 'thin', color: { argb: 'FFD3D3D3' } }
        };
    }
    currentRow++;

    worksheet.mergeCells(`A${currentRow}:${LAST_COL_LETTER}${currentRow}`);
    const razaoRow = worksheet.getRow(currentRow);
    razaoRow.getCell(1).value = headerInfo?.razaoSocial || '';
    razaoRow.getCell(1).font = { size: 10 };
    razaoRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
    for (let i = 1; i <= TOTAL_COLS; i++) {
        razaoRow.getCell(i).border = {
            bottom: { style: 'thin', color: { argb: 'FFD3D3D3' } }
        };
    }
    currentRow++;

    // ** CLIENTE, SAFRA, CULTURA **
    if (headerInfo?.cliente) {
        worksheet.getRow(currentRow).height = 10;
        currentRow++;

        worksheet.getRow(currentRow).height = 15;
        worksheet.mergeCells(`A${currentRow}:C${currentRow}`);
        worksheet.mergeCells(`D${currentRow}:F${currentRow}`);
        worksheet.mergeCells(`G${currentRow}:${LAST_COL_LETTER}${currentRow}`);

        const labelRow = worksheet.getRow(currentRow);
        const labelFont = { size: 9, bold: true, color: { argb: 'FF7B7B7B' } };
        const centerAlignment = { horizontal: 'center', vertical: 'middle' } as const;

        labelRow.getCell(1).value = 'CLIENTE';
        labelRow.getCell(4).value = 'SAFRA';
        labelRow.getCell(7).value = 'CULTURA';
        [1, 4, 7].forEach(c => { labelRow.getCell(c).font = labelFont; labelRow.getCell(c).alignment = centerAlignment; });
        currentRow++;

        worksheet.getRow(currentRow).height = 20;
        worksheet.mergeCells(`A${currentRow}:C${currentRow}`);
        worksheet.mergeCells(`D${currentRow}:F${currentRow}`);
        worksheet.mergeCells(`G${currentRow}:${LAST_COL_LETTER}${currentRow}`);

        const valueRow = worksheet.getRow(currentRow);
        const valueFont = { size: 11, bold: true, color: { argb: 'FF000000' } };

        valueRow.getCell(1).value = (headerInfo.cliente || '-').toUpperCase();
        valueRow.getCell(4).value = (headerInfo.safra || '-').toUpperCase();
        valueRow.getCell(7).value = (headerInfo.cultura || '-').toUpperCase();
        [1, 4, 7].forEach(c => { valueRow.getCell(c).font = valueFont; valueRow.getCell(c).alignment = centerAlignment; });

        const borderStyle: ExcelJS.Border = { style: 'thin', color: { argb: 'FF7B7B7B' } };
        const labelRowNum = currentRow - 1;
        const valueRowNum = currentRow;
        for (let col = 1; col <= TOTAL_COLS; col++) {
            worksheet.getRow(labelRowNum).getCell(col).border = {
                top: borderStyle,
                left: [1, 4, 7].includes(col) ? borderStyle : undefined,
                right: [3, 6, 8].includes(col) ? borderStyle : undefined,
            };
            worksheet.getRow(valueRowNum).getCell(col).border = {
                bottom: borderStyle,
                left: [1, 4, 7].includes(col) ? borderStyle : undefined,
                right: [3, 6, 8].includes(col) ? borderStyle : undefined,
            };
        }
        currentRow++;
    }

    currentRow++; // linha vazia

    const applyBorders = (rowNum: number, color = 'FF000000') => {
        const row = worksheet.getRow(rowNum);
        for (let i = 1; i <= TOTAL_COLS; i++) {
            row.getCell(i).border = {
                top: { style: 'thin', color: { argb: color } },
                left: { style: 'thin', color: { argb: color } },
                bottom: { style: 'thin', color: { argb: color } },
                right: { style: 'thin', color: { argb: color } }
            };
        }
    };

    const headerFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0E743F' } };
    const headerFont = { bold: true, color: { argb: 'FFFFFFFF' } };
    const centerAlign = { horizontal: 'center' as const, vertical: 'middle' as const };

    const addTableHeader = () => {
        const colHeaderRow = worksheet.getRow(currentRow);
        colHeaderRow.height = 20;

        const headers = [
            { col: 1, label: 'Insumo' },
            { col: 2, label: 'Tipo' },
            { col: 3, label: 'Classe Agronômica' },
            { col: 4, label: 'Composição' },
            { col: 5, label: 'Quantidade' },
        ];

        // Mescla as últimas colunas de padding com Quantidade
        worksheet.mergeCells(`E${currentRow}:${LAST_COL_LETTER}${currentRow}`);

        headers.forEach(({ col, label }) => {
            const cell = colHeaderRow.getCell(col);
            cell.value = label;
            cell.font = headerFont;
            cell.fill = headerFill;
            cell.alignment = centerAlign;
        });

        // Preenche colunas de padding com a cor do header
        for (let i = 6; i <= TOTAL_COLS; i++) {
            colHeaderRow.getCell(i).fill = headerFill;
        }

        applyBorders(currentRow, 'FF000000');
        currentRow++;
    };

    const addInsumoRow = (insu: typeInsumos, zebraColor: string) => {
        const insuRow = worksheet.getRow(currentRow);
        insuRow.height = 25;

        worksheet.mergeCells(`E${currentRow}:${LAST_COL_LETTER}${currentRow}`);

        insuRow.getCell(1).value = insu.insumo_nome || '-';
        insuRow.getCell(2).value = insu.nome_tipo || '-';
        insuRow.getCell(3).value = insu.classe_agronomica_nome || '-';
        insuRow.getCell(4).value = formatarComposicao(insu.composicao, insu.composicao_bioinsumo);
        insuRow.getCell(5).value = insu.quantidade_total_view || '-';

        for (let i = 1; i <= TOTAL_COLS; i++) {
            const cell = insuRow.getCell(i);
            cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: zebraColor } };
        }

        applyBorders(currentRow, 'FF000000');
        currentRow++;
    };

    const isJuntarInsumos = dados.length > 0 && !('insumos' in dados[0]);

    if (isJuntarInsumos) {
        addTableHeader();

        let globalInsumoIndex = 0;
        (dados as typeInsumos[]).forEach((insu) => {
            const zebraColor = (globalInsumoIndex % 2 === 0) ? 'FFFFFFFF' : 'FFE5F1E1';
            addInsumoRow(insu, zebraColor);
            globalInsumoIndex++;
        });

        if (dados.length === 0) {
            worksheet.mergeCells(`A${currentRow}:${LAST_COL_LETTER}${currentRow}`);
            const emptyRow = worksheet.getRow(currentRow);
            emptyRow.getCell(1).value = 'Nenhum insumo planejado.';
            emptyRow.getCell(1).font = { italic: true };
            emptyRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
            emptyRow.height = 25;
            applyBorders(currentRow, 'FF000000');
            currentRow++;
        }

    } else {
        (dados as typeEstruturaCompraInsumos[]).forEach((propriedade) => {
            worksheet.mergeCells(`A${currentRow}:${LAST_COL_LETTER}${currentRow}`);
            const propRow = worksheet.getRow(currentRow);
            propRow.getCell(1).value = propriedade.nome_propriedade || "PROPRIEDADE NÃO INFORMADA";
            propRow.getCell(1).font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 };
            propRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF5DA57D' } };
            propRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
            propRow.height = 25;
            applyBorders(currentRow, 'FF165C13');
            currentRow++;

            addTableHeader();

            let globalInsumoIndex = 0;
            propriedade.insumos.forEach((insu) => {
                const zebraColor = (globalInsumoIndex % 2 === 0) ? 'FFFFFFFF' : 'FFE5F1E1';
                addInsumoRow(insu, zebraColor);
                globalInsumoIndex++;
            });

            if (propriedade.insumos.length === 0) {
                worksheet.mergeCells(`A${currentRow}:${LAST_COL_LETTER}${currentRow}`);
                const emptyRow = worksheet.getRow(currentRow);
                emptyRow.getCell(1).value = 'Nenhum insumo planejado para esta propriedade.';
                emptyRow.getCell(1).font = { italic: true };
                emptyRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
                emptyRow.height = 25;
                applyBorders(currentRow, 'FF000000');
                currentRow++;
            }

            currentRow++; // espaço entre propriedades
        });
    }

    // ** FOOTER **
    const espacoRow = worksheet.addRow([]);
    espacoRow.height = 20;

    const email_usuario_logado = (secureLocalStorage.getItem('Itens') as any)?.[0]?.email?.toLowerCase() || '';
    const footerRow = worksheet.addRow([]);
    footerRow.getCell(1).value = `Gerado por: ${email_usuario_logado} - ${dayjs().format('DD/MM/YYYY HH:mm:ss')}`;
    footerRow.font = { italic: true };
    footerRow.alignment = { horizontal: 'center', vertical: 'middle' };

    for (let i = 1; i <= TOTAL_COLS; i++) {
        worksheet.getCell(`${String.fromCharCode(64 + i)}${footerRow.number}`).border = {
            top: { style: 'thin', color: { argb: 'FF5DA57D' } }
        };
    }
    worksheet.mergeCells(`A${footerRow.number}:${LAST_COL_LETTER}${footerRow.number}`);
    footerRow.height = 25;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `Relatorio_Compra_Insumos_${dayjs().format("DD_MM_YYYY_HH_mm")}.xlsx`);
};
